import { useState } from 'react';
import { api } from './lib/api';

// An ambassador's page (#598): their code, link, terms and numbers. Since #736 it's only inside their ClassGuardian
// account (the Ambassador tab); the private links and email sign-in are retired.

export interface PartnerPageData {
  name: string;
  code: string;
  link: string;
  active: boolean;
  discountPct: number;
  discountMonths: number;
  commissionPct: number;
  commissionMonths: number;
  holdDays: number;
  minPayoutCents: number;
  families: number;
  paying: number;
  waitingCents: number;
  owedCents: number;
  paidCents: number;
  paidThisYearCents: number;
  payouts: { amountCents: number; method: string; paidAt: number }[];
  contact: string;
  /** Payouts through Stripe Connect (#725). */
  stripe?: { available: boolean; state: 'none' | 'pending' | 'ready'; bankLast4: string };
  /** Inactivity (#895): while they've been warned, and once their part has ended. */
  warning?: { endsAt: number; accessEndsAt: number } | null;
  ended?: { reason: string; accessEndsAt: number } | null;
  /** Their tier and what the next one takes (#902); sign-ups count once the family has paid. */
  progress?: {
    tier: { name: string; commissionPct: number; discountPct: number } | null;
    signups: number;
    followers: number;
    next: { name: string; commissionPct: number; signups: number; followers: number; signupsToGo: number } | null;
  };
  followersPending?: number | null;
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const months = (n: number) => (n === 1 ? 'month' : `${n} months`);
const longDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

/** An ambassador's code, terms and numbers, on the Brand Ambassador page in their account (#721). */
export function AmbassadorDashboard({ data, inApp = false, stripeApi }: { data: PartnerPageData; inApp?: boolean; stripeApi: string }) {
  const [copied, setCopied] = useState('');
  const copy = (what: string, text: string) => navigator.clipboard.writeText(text).then(() => setCopied(what), () => undefined);
  return (
    <>
      {inApp ? (
        <h1 className="row-gap">
          Brand Ambassador <span className={`tag ${data.active ? 'good' : ''}`}>{data.active ? 'Active' : data.ended ? 'Ended' : 'Paused'}</span>
        </h1>
      ) : (
        <h1>Hi, {data.name}</h1>
      )}
      {!data.active && !data.ended && <p className="warn-text">Your code is paused right now, so new families can’t use it. Your earnings are unchanged.</p>}
      {data.warning && (
        <section className="card stack amb-notice">
          <strong>Your code needs a new family by {longDay(data.warning.endsAt)}</strong>
          <span>
            No new family has signed up with {data.code} in a while. If one signs up by {longDay(data.warning.endsAt)}, you stay in the program. If not, your
            part ends, and you keep earning on the families you already brought in. Your own family’s no-charge access would end on{' '}
            {longDay(data.warning.accessEndsAt)}.
          </span>
        </section>
      )}
      {data.ended && (
        <section className="card stack amb-notice">
          <strong>Your part in the program has ended</strong>
          <span>
            {data.ended.reason === 'inactive' ? 'No new family signed up with your code in time, so ' : ''}
            {data.code} no longer works for new families. You keep earning on the families you brought in, and your earnings and payouts stay here.
            {data.ended.accessEndsAt ? ` Your own family’s no-charge access ends on ${longDay(data.ended.accessEndsAt)}; then choose a plan in Settings → Plan & billing.` : ''}
          </span>
        </section>
      )}
      <section className="card stack">
        <span className="row-gap wrap">
          Your code: <code className="partner-code big">{data.code}</code>
          <button type="button" className="link-btn" onClick={() => copy('code', data.code)}>
            {copied === 'code' ? 'Copied' : 'Copy'}
          </button>
        </span>
        <span className="row-gap wrap">
          Your link: <code className="break">{data.link}</code>
          <button type="button" className="link-btn" onClick={() => copy('link', data.link)}>
            {copied === 'link' ? 'Copied' : 'Copy'}
          </button>
        </span>
        <p className="muted">
          {data.discountPct > 0 ? `Families get ${data.discountPct}% off their first ${months(data.discountMonths)}. ` : ''}
          You earn {data.commissionPct}% of what they pay for their first {months(data.commissionMonths)}. Earnings are ready {data.holdDays} days after each
          payment, and paid out from {money(data.minPayoutCents)}.
        </p>
      </section>
      <section className="partner-totals">
        <div>
          <span className="muted small">Families signed up</span>
          <strong>{data.families}</strong>
        </div>
        <div>
          <span className="muted small">Paying now</span>
          <strong>{data.paying}</strong>
        </div>
        <div>
          <span className="muted small">Earned, waiting {data.holdDays} days</span>
          <strong>{money(data.waitingCents)}</strong>
        </div>
        <div>
          <span className="muted small">Ready to be paid</span>
          <strong className="good-text">{money(data.owedCents)}</strong>
        </div>
      </section>
      {data.progress?.tier && <TierProgress data={data} api={stripeApi.replace(/\/stripe$/, '')} />}
      {data.stripe?.available && data.stripe.state !== 'ready' && data.owedCents > 0 && (
        <p className="amb-waiting">
          <strong>You have {money(data.owedCents)} waiting.</strong> Set up payouts below so we can send it.
        </p>
      )}
      {data.stripe?.available && <PayoutSetup stripe={data.stripe} api={stripeApi} />}
      <section className="card stack">
        <h2>Payments to you</h2>
        {data.payouts.length === 0 ? (
          <p className="muted">None yet.</p>
        ) : (
          data.payouts.map((p, i) => (
            <span key={i}>
              {new Date(p.paidAt).toLocaleDateString()} · {money(p.amountCents)}
              {p.method ? ` · ${p.method}` : ''}
            </span>
          ))
        )}
        <span className="muted small">
          Paid so far: {money(data.paidCents)} · this year: {money(data.paidThisYearCents)}
        </span>
      </section>
      <p className="muted small">
        Families are shown as counts only, never by name. The{' '}
        <a href="/ambassador-terms">Brand Ambassador terms</a> apply.
        {data.contact && (
          <>
            {' '}
            Questions: <a href={`mailto:${data.contact}`}>{data.contact}</a>
          </>
        )}
      </p>
    </>
  );
}

/**
 * Payouts through Stripe Connect (#725): set up on Stripe's own pages (identity, bank, tax ID), then Stripe pays it
 * out and sends the 1099. ClassGuardian never sees the bank or tax details.
 */
/** Their tier, how close the next one is, and their follower count (#902). */
function TierProgress({ data, api: base }: { data: PartnerPageData; api: string }) {
  const pr = data.progress!;
  const t = pr.tier!;
  const n = pr.next;
  const [followers, setFollowers] = useState('');
  const [pending, setPending] = useState<number | null>(data.followersPending ?? null);
  const [error, setError] = useState('');
  const pct = n?.signups ? Math.min(100, Math.round((pr.signups / n.signups) * 100)) : 100;
  async function send() {
    setError('');
    try {
      const r = await api.post<PartnerPageData>(`${base}/followers`, { followers: Math.max(0, parseInt(followers.replace(/[^0-9]/g, ''), 10) || 0) });
      setPending(r.followersPending ?? null);
      setFollowers('');
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <section className="card stack">
      <div className="row-between wrap">
        <h2>
          You’re {t.name}: you earn {t.commissionPct}%
        </h2>
        <span className="muted small">Families get {t.discountPct}% off</span>
      </div>
      {n ? (
        <>
          {n.signups > 0 && (
            <div className="tier-bar" role="progressbar" aria-valuenow={pr.signups} aria-valuemin={0} aria-valuemax={n.signups} aria-label={`Paid sign-ups toward ${n.name}`}>
              <span style={{ width: `${pct}%` }} />
            </div>
          )}
          <span>
            {n.signups > 0 && (
              <>
                {pr.signups} of {n.signups} paid sign-ups to <strong>{n.name} ({n.commissionPct}%)</strong>.{' '}
              </>
            )}
            {n.followers > 0 && <>Or reach over {n.followers.toLocaleString()} followers.</>}
          </span>
        </>
      ) : (
        <span>This is our top tier. Thank you!</span>
      )}
      <span className="muted small">
        A family counts toward your tier once it makes its first payment; free trials that don’t pay don’t count. Moving up raises what you earn on every
        payment from then on, including families you already brought in.
      </span>
      <div className="row-gap wrap">
        <span className="small">
          Your followers: <strong>{pr.followers.toLocaleString()}</strong>
          {pending !== null && <span className="muted"> · {pending.toLocaleString()} waiting for us to check</span>}
        </span>
        <input inputMode="numeric" value={followers} onChange={(e) => setFollowers(e.target.value.replace(/[^0-9,]/g, ''))} placeholder="New count" aria-label="New follower count" maxLength={13} />
        <button type="button" className="btn small" disabled={!followers} onClick={() => void send()}>
          Update
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function PayoutSetup({ stripe, api: base }: { stripe: NonNullable<PartnerPageData['stripe']>; api: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async (kind: 'setup' | 'dashboard') => {
    setBusy(true);
    setError('');
    try {
      window.location.assign((await api.post<{ url: string }>(`${base}/${kind}`)).url);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <section className="card stack">
      <div className="row-between wrap">
        <h2>Payouts</h2>
        <span className={`tag ${stripe.state === 'ready' ? 'good' : ''}`}>
          {stripe.state === 'ready' ? 'Ready' : stripe.state === 'pending' ? 'Stripe is checking your details' : 'Not set up'}
        </span>
      </div>
      {stripe.state === 'ready' ? (
        <p className="muted">
          Payouts go to your bank account{stripe.bankLast4 ? ` ending ${stripe.bankLast4}` : ''}. Your tax form comes from Stripe, our payment provider.
        </p>
      ) : stripe.state === 'pending' ? (
        <p className="muted">Stripe needs a little more before you can be paid. It only takes a few minutes.</p>
      ) : (
        <p className="muted">
          We pay you through Stripe. Stripe asks for your name, bank account and tax ID (for your 1099) on its own secure pages; ClassGuardian never sees
          your tax ID or bank details.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      <span className="row-gap wrap">
        {stripe.state !== 'ready' && (
          <button type="button" className="btn primary" disabled={busy} onClick={() => void go('setup')}>
            {stripe.state === 'none' ? 'Set up payouts with Stripe' : 'Finish with Stripe'}
          </button>
        )}
        {stripe.state !== 'none' && (
          <button type="button" className="btn" disabled={busy} onClick={() => void go('dashboard')}>
            View my payouts in Stripe
          </button>
        )}
      </span>
    </section>
  );
}
